import { useCallback } from "react";
import { useUnit } from "effector-react";
import { button, useControls } from "leva";
import type { Schema } from "leva/dist/declarations/src/types/public.js";
import type { CameraConfig } from "../../cameraConfig";
import type { CollisionConfig } from "../../scene/collision/CollisionSystem";
import type { DevConfig } from "../../devConfig";
import type { PoiConfig } from "../../poiConfig";
import type { AudioConfig } from "../../state/appConfig";
import type { TellerAssistantConfig } from "../../tellerAssistantConfig";
import { MUSIC_TRACKS } from "../../audio/musicTracks";
import { ASSISTANT_VOICE_OPTIONS } from "../../audio/assistantVoices";
import {
  $audioConfig,
  $backgroundConfig,
  $cameraConfig,
  $collisionConfig,
  $devConfig,
  $poiConfig,
  $previewModelConfig,
  $tellerAssistantConfig,
  resetAppConfig,
  updateAudioConfig,
  updateBackgroundConfig,
  updateCameraConfig,
  updateCollisionConfig,
  updateDevConfig,
  updatePoiConfig,
  updatePreviewModelConfig,
  type BackgroundConfigState,
  type PreviewModelConfig,
  updateTellerAssistantConfig,
} from "../../state/appConfig";
import { $viewMode, setViewMode } from "../../state/viewMode";
import { $locale, setLocale } from "../../state/locale";

type NumberControlMetadata = {
  type: "number";
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  hint?: string;
};

type SelectControlMetadata = {
  type: "select";
  options: Record<string, string>;
  label?: string;
  hint?: string;
};

type StringControlMetadata = {
  type: "string";
  label?: string;
  hint?: string;
};

type BooleanControlMetadata = {
  type?: "boolean";
  label?: string;
  hint?: string;
};

type ControlMetadata =
  | NumberControlMetadata
  | BooleanControlMetadata
  | SelectControlMetadata
  | StringControlMetadata;
type ConfigKey<T> = Extract<keyof T, string>;
type ConfigMetadata<T extends object> = Partial<
  Record<ConfigKey<T>, ControlMetadata>
>;

const CAMERA_METADATA = {
  fov: {
    type: "number",
    label: "Field of View",
    min: 10,
    max: 120,
    step: 1,
  },
  lookSpeedHorizontal: {
    type: "number",
    label: "Look Speed X",
    min: 1,
    max: 360,
    step: 1,
  },
  lookSpeedVertical: {
    type: "number",
    label: "Look Speed Y",
    min: 1,
    max: 360,
    step: 1,
  },
  dragInertia: {
    type: "number",
    label: "Drag Inertia",
    min: 0,
    max: 0.999,
    step: 0.001,
  },
  movementSpeed: {
    type: "number",
    label: "Move Speed",
    min: 0,
    max: 32,
    step: 0.05,
  },
  orbitAutoMove: {
    type: "boolean",
    label: "Auto Orbit",
    hint: "Continuously orbit around the target while in orbit view",
  },
  orbitAutoMoveDirection: {
    type: "select",
    label: "Orbit Direction",
    options: {
      Left: "left",
      Right: "right",
    },
  },
  orbitAutoMoveSpeed: {
    type: "number",
    label: "Orbit Speed",
    hint: "Angular speed (radians/sec) when auto-orbit is enabled",
    min: 0,
    max: 5,
    step: 0.01,
  },
  orbitMinDistance: {
    type: "number",
    label: "Orbit Min Dist",
    hint: "Closest camera distance allowed in orbit view",
    min: 0,
    max: 50,
    step: 0.01,
  },
  orbitMaxDistance: {
    type: "number",
    label: "Orbit Max Dist",
    hint: "Farthest camera distance allowed in orbit view",
    min: 0,
    max: 50,
    step: 0.01,
  },
  orbitMinPolarAngle: {
    type: "number",
    label: "Orbit Min Polar",
    hint: "Minimum vertical angle (radians) for orbit",
    min: 0,
    max: Math.PI,
    step: 0.01,
  },
  orbitMaxPolarAngle: {
    type: "number",
    label: "Orbit Max Polar",
    hint: "Maximum vertical angle (radians) for orbit",
    min: 0,
    max: Math.PI,
    step: 0.01,
  },
  heightCoef: {
    type: "number",
    label: "Height Mult",
    min: 0,
    max: 10,
    step: 0.1,
  },
  speedHeightMin: {
    type: "number",
    label: "Min Height Spd",
    min: -2,
    max: 2,
    step: 0.01,
  },
  speedHeightMax: {
    type: "number",
    label: "Max Height Spd",
    min: -2,
    max: 2,
    step: 0.01,
  },
  pinchMoveSpeed: {
    type: "number",
    label: "Pinch Speed",
    min: 0,
    max: 0.05,
    step: 0.0005,
  },
  verticalMin: {
    type: "number",
    label: "Vertical Min",
    min: 0,
    max: Math.PI,
    step: 0.01,
  },
  verticalMax: {
    type: "number",
    label: "Vertical Max",
    min: 0,
    max: Math.PI,
    step: 0.01,
  },
  clampMinHeight: {
    type: "number",
    label: "Clamp Min H",
    min: -10,
    max: 10,
    step: 0.01,
  },
  clampMaxHeight: {
    type: "number",
    label: "Clamp Max H",
    min: -10,
    max: 100,
    step: 0.01,
  },
  clampMaxHeightFar: {
    type: "number",
    label: "Clamp Max H (Far)",
    hint: "Maximum camera Y at the farthest orbit zoom distance",
    min: -10,
    max: 100,
    step: 0.01,
  },
} satisfies ConfigMetadata<CameraConfig>;

const COLLISION_METADATA = {
  cameraRadius: {
    type: "number",
    label: "Camera Radius",
    min: 0,
    max: 0.5,
    step: 0.001,
  },
  maxDistance: {
    type: "number",
    label: "Raycast Range",
    min: 0.1,
    max: 10,
    step: 0.1,
  },
  epsilon: {
    type: "number",
    label: "Epsilon",
    min: 0,
    max: 0.01,
    step: 0.0001,
  },
} satisfies ConfigMetadata<CollisionConfig>;

const DEV_METADATA = {
  showReferenceGrid: {
    type: "boolean",
    label: "Reference Grid",
    hint: "Toggle the floor/ceiling guides that follow the clamp heights",
  },
  showGlobalMapReferenceGrid: {
    type: "boolean",
    label: "Global Map Grid + Axes",
    hint: "Toggle the grid + axes helpers on the global map view",
  },
  showCollisionBounds: {
    type: "boolean",
    label: "Collision Bounds",
    hint: "Outline all collision helpers (green AABBs + hidden FBX volumes)",
  },
  showSightMarkers: {
    type: "boolean",
    label: "Sight Markers",
    hint: "Display magenta spheres for every sight anchor in the scene",
  },
  showOrbitCenterMarker: {
    type: "boolean",
    label: "Orbit Center Marker",
    hint: "Render a helper sphere at the orbit pivot to aid debugging",
  },
  showTellerAssistant: {
    type: "boolean",
    label: "Teller Assistant",
    hint: "Render the teller assistant companion model",
  },
  showRearView: {
    type: "boolean",
    label: "Rear View",
    hint: "Display a rear-facing Spark render overlay on the camera",
  },
  assistantApiHost: {
    type: "string",
    label: "Assistant API Host",
    hint: "Override the host (origin) for /api/assistant requests",
  },
  assistantApiToken: {
    type: "string",
    label: "Assistant API Token",
    hint: "Bearer token for assistant requests (optional)",
  },
} satisfies ConfigMetadata<DevConfig>;

const POI_METADATA = {
  showVisibleOnly: {
    type: "boolean",
    label: "Hide Occluded",
    hint: "Only render POI markers if their sight is visible from the camera",
  },
  occlusionProbeMaxOffset: {
    type: "number",
    label: "Probe Offset Max",
    hint: "Maximum distance to pull the occlusion probe toward the camera",
    min: 0,
    max: 1,
    step: 0.01,
  },
  occlusionProbeMinDistance: {
    type: "number",
    label: "Probe Distance Min",
    hint: "Closest allowed distance between the probe and the POI center",
    min: 0,
    max: 0.5,
    step: 0.001,
  },
} satisfies ConfigMetadata<PoiConfig>;

const AUDIO_METADATA = {
  soundOn: {
    type: "boolean",
    label: "Sound On",
  },
  musicOn: {
    type: "boolean",
    label: "Music On",
  },
  voiceOn: {
    type: "boolean",
    label: "Voice On",
  },
  voiceVolume: {
    type: "number",
    label: "Voice Volume",
    min: 0,
    max: 1,
    step: 0.01,
  },
  assistantVoiceName: {
    type: "select",
    label: "Assistant Voice",
    options: Object.fromEntries(
      ASSISTANT_VOICE_OPTIONS.map((voice) => [voice, voice])
    ),
  },
  musicTrackId: {
    type: "select",
    label: "Music Track",
    options: Object.fromEntries(
      MUSIC_TRACKS.map((track) => [track.label, track.id])
    ),
  },
  musicVolume: {
    type: "number",
    label: "Music Volume",
    min: 0,
    max: 1,
    step: 0.01,
  },
} satisfies ConfigMetadata<AudioConfig>;

const LOCALE_OPTIONS = {
  English: "en",
  Arabic: "ar",
} as const;

const formatLabel = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^\w/, (match) => match.toUpperCase());

const isNumberMetadata = (
  metadata?: ControlMetadata
): metadata is NumberControlMetadata => metadata?.type === "number";

const isSelectMetadata = (
  metadata?: ControlMetadata
): metadata is SelectControlMetadata => metadata?.type === "select";

const isStringMetadata = (
  metadata?: ControlMetadata
): metadata is StringControlMetadata => metadata?.type === "string";

type ChangeContext = {
  initial?: boolean;
};

const createSectionSchema = (
  values: object,
  metadata: Partial<Record<string, ControlMetadata>>,
  onChange: (key: string, value: number | boolean | string) => void
): Schema => {
  const schema: Schema = {};

  for (const [key, rawValue] of Object.entries(values)) {
    const controlMetadata = metadata[key];
    const label = controlMetadata?.label ?? formatLabel(key);
    const hint = controlMetadata?.hint;

    if (typeof rawValue === "number") {
      schema[key] = {
        value: rawValue,
        label,
        hint,
        min: isNumberMetadata(controlMetadata)
          ? controlMetadata.min
          : undefined,
        max: isNumberMetadata(controlMetadata)
          ? controlMetadata.max
          : undefined,
        step: isNumberMetadata(controlMetadata)
          ? controlMetadata.step
          : undefined,
        onChange: (
          nextValue: number,
          _path: string,
          context: ChangeContext
        ) => {
          if (context.initial) {
            return;
          }
          onChange(key, nextValue);
        },
      };
    } else if (typeof rawValue === "boolean") {
      schema[key] = {
        value: rawValue,
        label,
        hint,
        onChange: (
          nextValue: boolean,
          _path: string,
          context: ChangeContext
        ) => {
          if (context.initial) {
            return;
          }
          onChange(key, nextValue);
        },
      };
    } else if (typeof rawValue === "string") {
      if (isSelectMetadata(controlMetadata)) {
        schema[key] = {
          value: rawValue,
          label,
          hint,
          options: controlMetadata.options,
          onChange: (
            nextValue: string,
            _path: string,
            context: ChangeContext
          ) => {
            if (context.initial) {
              return;
            }
            onChange(key, nextValue);
          },
        };
      } else if (isStringMetadata(controlMetadata)) {
        schema[key] = {
          value: rawValue,
          label,
          hint,
          onChange: (
            nextValue: string,
            _path: string,
            context: ChangeContext
          ) => {
            if (context.initial) {
              return;
            }
            onChange(key, nextValue);
          },
        };
      }
    }
  }

  return schema;
};

export function AppConfigPanel() {
  const { cameraConfig, collisionConfig, devConfig, poiConfig, audioConfig, viewMode, locale, previewModelConfig, backgroundConfig, tellerAssistantConfig } = useUnit({
    cameraConfig: $cameraConfig,
    collisionConfig: $collisionConfig,
    devConfig: $devConfig,
    poiConfig: $poiConfig,
    audioConfig: $audioConfig,
    tellerAssistantConfig: $tellerAssistantConfig,
    viewMode: $viewMode,
    locale: $locale,
    previewModelConfig: $previewModelConfig,
    backgroundConfig: $backgroundConfig,
  });
  const [
    pushAudioConfig,
    pushCameraConfig,
    pushCollisionConfig,
    pushDevConfig,
    pushPoiConfig,
    pushTellerAssistantConfig,
    resetConfig,
    changeViewMode,
    changeLocale,
    pushPreviewModelConfig,
    pushBackgroundConfig,
  ] =
    useUnit([
      updateAudioConfig,
      updateCameraConfig,
      updateCollisionConfig,
      updateDevConfig,
      updatePoiConfig,
      updateTellerAssistantConfig,
      resetAppConfig,
      setViewMode,
      setLocale,
      updatePreviewModelConfig,
      updateBackgroundConfig,
  ]);

  const handleCameraChange = useCallback(
    (key: string, value: number | boolean | string) => {
      const nextConfig = {
        ...cameraConfig,
        [key]: value,
      };
      pushCameraConfig(nextConfig);
    },
    [cameraConfig, pushCameraConfig]
  );

  const handleOrbitCenterChange = useCallback(
    (axis: 'x' | 'y' | 'z', value: number) => {
      const nextConfig = {
        ...cameraConfig,
        orbitCenter: {
          ...cameraConfig.orbitCenter,
          [axis]: value,
        },
      };
      pushCameraConfig(nextConfig);
    },
    [cameraConfig, pushCameraConfig]
  );

  const handleViewOffsetChange = useCallback(
    (key: keyof CameraConfig['viewOffset'], value: number | boolean) => {
      const nextConfig = {
        ...cameraConfig,
        viewOffset: {
          ...cameraConfig.viewOffset,
          [key]: value,
        },
      };
      pushCameraConfig(nextConfig);
    },
    [cameraConfig, pushCameraConfig]
  );

  const handleCollisionChange = useCallback(
    (key: string, value: number | boolean | string) => {
      if (typeof value === "string") {
        return;
      }
      const nextConfig = {
        ...collisionConfig,
        [key]: value,
      };
      pushCollisionConfig(nextConfig);
    },
    [collisionConfig, pushCollisionConfig]
  );

  const handleDevChange = useCallback(
    (key: string, value: number | boolean | string) => {
      const nextConfig = {
        ...devConfig,
        [key]: value,
      };
      pushDevConfig(nextConfig);
    },
    [devConfig, pushDevConfig]
  );

  const handlePoiChange = useCallback(
    (key: string, value: number | boolean | string) => {
      if (typeof value === "string") {
        return;
      }
      const nextConfig = {
        ...poiConfig,
        [key]: value,
      };
      pushPoiConfig(nextConfig);
    },
    [poiConfig, pushPoiConfig]
  );

  const handleAudioChange = useCallback(
    (key: string, value: number | boolean | string) => {
      const nextConfig = {
        ...audioConfig,
        [key]: value,
      };
      pushAudioConfig(nextConfig);
    },
    [audioConfig, pushAudioConfig]
  );

  const handlePreviewWavesChange = useCallback(
    (key: keyof PreviewModelConfig, value: number) => {
      pushPreviewModelConfig({ [key]: value });
    },
    [pushPreviewModelConfig]
  );

  const handleWavesCenterChange = useCallback(
    (axis: 'x' | 'y' | 'z', value: number) => {
      pushPreviewModelConfig({
        wavesCenter: {
          ...previewModelConfig.wavesCenter,
          [axis]: value,
        },
      });
    },
    [previewModelConfig.wavesCenter, pushPreviewModelConfig]
  );

  const handleBackgroundChange = useCallback(
    (key: keyof BackgroundConfigState, value: number | boolean | string | [number, number, number]) => {
      pushBackgroundConfig({ [key]: value });
    },
    [pushBackgroundConfig]
  );

  const handleSkyboxRotationChange = useCallback(
    (axis: 0 | 1 | 2, value: number) => {
      const newRotation: [number, number, number] = [...backgroundConfig.skyboxRotation];
      newRotation[axis] = value;
      pushBackgroundConfig({ skyboxRotation: newRotation });
    },
    [backgroundConfig.skyboxRotation, pushBackgroundConfig]
  );
  const handleTellerAssistantTransformChange = useCallback(
    (key: 'position' | 'rotationY' | 'scale' | 'hoverAmplitude', value: number | { x: number; y: number; z: number }) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        transform: {
          ...tellerAssistantConfig.transform,
          [key]: value,
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantPositionChange = useCallback(
    (axis: 'x' | 'y' | 'z', value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        transform: {
          ...tellerAssistantConfig.transform,
          position: {
            ...tellerAssistantConfig.transform.position,
            [axis]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantDesktopChange = useCallback(
    (key: 'scale' | 'hoverAmplitude' | 'paddingRight' | 'verticalOffset', value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        transform: {
          ...tellerAssistantConfig.transform,
          desktop: {
            ...tellerAssistantConfig.transform.desktop,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantMobileChange = useCallback(
    (key: 'scale' | 'hoverAmplitude' | 'paddingRight' | 'verticalOffset', value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        transform: {
          ...tellerAssistantConfig.transform,
          mobile: {
            ...tellerAssistantConfig.transform.mobile,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantMeshChange = useCallback(
    (key: string, value: number | boolean | string) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        mesh: {
          ...tellerAssistantConfig.mesh,
          [key]: value,
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantShellChange = useCallback(
    (key: string, value: number | string) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        shell: {
          ...tellerAssistantConfig.shell,
          [key]: value,
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantLightingTypeChange = useCallback(
    (lightingType: 'simple' | 'env') => {
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          lightingType,
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantSimpleLightChange = useCallback(
    (axis: 'lightX' | 'lightY' | 'lightZ', value: number) => {
      const key = axis === 'lightX' ? 'lightX' : axis === 'lightY' ? 'lightY' : 'lightZ';
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          simple: {
            ...tellerAssistantConfig.lighting.simple,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantSimpleLightColorChange = useCallback(
    (key: 'ambientLightColor' | 'directionalLightColor', value: string) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          simple: {
            ...tellerAssistantConfig.lighting.simple,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantSimpleLightIntensityChange = useCallback(
    (key: 'ambientLightIntensity' | 'directionalLightIntensity', value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          simple: {
            ...tellerAssistantConfig.lighting.simple,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );

  const handleTellerAssistantEnvLightChange = useCallback(
    (key: keyof TellerAssistantConfig['lighting']['env'], value: number) => {
      const nextConfig = {
        ...tellerAssistantConfig,
        lighting: {
          ...tellerAssistantConfig.lighting,
          env: {
            ...tellerAssistantConfig.lighting.env,
            [key]: value,
          },
        },
      };
      pushTellerAssistantConfig(nextConfig);
    },
    [tellerAssistantConfig, pushTellerAssistantConfig]
  );


  useControls(
    "Localization",
    () => ({
      Language: {
        value: locale,
        options: LOCALE_OPTIONS,
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          changeLocale(nextValue as typeof locale);
        },
      },
    }),
    [changeLocale, locale]
  );

  useControls(
    "Camera",
    () =>
      createSectionSchema(cameraConfig, CAMERA_METADATA, handleCameraChange),
    [cameraConfig, handleCameraChange]
  );

  useControls(
    "Orbit Center",
    () => ({
      "Center X": {
        value: cameraConfig.orbitCenter.x,
        min: -5,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleOrbitCenterChange('x', nextValue);
        },
      },
      "Center Y": {
        value: cameraConfig.orbitCenter.y,
        min: -5,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleOrbitCenterChange('y', nextValue);
        },
      },
      "Center Z": {
        value: cameraConfig.orbitCenter.z,
        min: -5,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleOrbitCenterChange('z', nextValue);
        },
      },
    }),
    [cameraConfig, handleOrbitCenterChange]
  );

  useControls(
    "View Offset",
    () => ({
      Enabled: {
        value: cameraConfig.viewOffset.enabled,
        onChange: (nextValue: boolean, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('enabled', nextValue);
        },
      },
      Top: {
        value: cameraConfig.viewOffset.top,
        min: 0,
        max: 2000,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('top', nextValue);
        },
      },
      Right: {
        value: cameraConfig.viewOffset.right,
        min: 0,
        max: 2000,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('right', nextValue);
        },
      },
      Bottom: {
        value: cameraConfig.viewOffset.bottom,
        min: 0,
        max: 2000,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('bottom', nextValue);
        },
      },
      Left: {
        value: cameraConfig.viewOffset.left,
        min: 0,
        max: 2000,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) {
            return;
          }
          handleViewOffsetChange('left', nextValue);
        },
      },
    }),
    [cameraConfig.viewOffset, handleViewOffsetChange]
  );

  useControls(
    "Collision",
    () =>
      createSectionSchema(
        collisionConfig,
        COLLISION_METADATA,
        handleCollisionChange
      ),
    [collisionConfig, handleCollisionChange]
  );

  useControls(
    "Dev Tools",
    () => createSectionSchema(devConfig, DEV_METADATA, handleDevChange),
    [devConfig, handleDevChange]
  );

  useControls(
    "Camera Controls",
    () => ({
      "View Mode": {
        value: viewMode,
        options: {
          "First-person": "first-person",
          Orbit: "orbit",
        },
        onChange: (
          nextValue: "first-person" | "orbit",
          _path: string,
          context: ChangeContext
        ) => {
          if (context.initial) {
            return;
          }
          changeViewMode(nextValue);
        },
      },
    }),
    [viewMode, changeViewMode]
  );
  useControls(
    "Points of Interest",
    () => createSectionSchema(poiConfig, POI_METADATA, handlePoiChange),
    [poiConfig, handlePoiChange]
  );

  useControls(
    "Audio",
    () => createSectionSchema(audioConfig, AUDIO_METADATA, handleAudioChange),
    [audioConfig, handleAudioChange]
  );

  useControls(
    "Background",
    () => ({
      Color: {
        value: '#' + backgroundConfig.color.toString(16).padStart(6, '0'),
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          const colorNum = parseInt(nextValue.replace('#', ''), 16);
          handleBackgroundChange('color', colorNum);
        },
      },
      Skybox: {
        value: backgroundConfig.skyboxEnabled,
        onChange: (nextValue: boolean, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleBackgroundChange('skyboxEnabled', nextValue);
        },
      },
      "Skybox URL": {
        value: backgroundConfig.skyboxUrl,
        label: "Skybox URL",
        hint: "Absolute URL for the skybox texture (equirectangular image)",
        render: (get: (path: string) => boolean) => get('Background.Skybox'),
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleBackgroundChange('skyboxUrl', nextValue);
        },
      },
      Intensity: {
        value: backgroundConfig.skyboxIntensity,
        min: 0,
        max: 1,
        step: 0.01,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleBackgroundChange('skyboxIntensity', nextValue);
        },
      },
      Blur: {
        value: backgroundConfig.skyboxBlur,
        min: 0,
        max: 1,
        step: 0.01,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleBackgroundChange('skyboxBlur', nextValue);
        },
      },
      "Rotation X": {
        value: backgroundConfig.skyboxRotation[0],
        min: 0,
        max: 360,
        step: 1,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleSkyboxRotationChange(0, nextValue);
        },
      },
      "Rotation Y": {
        value: backgroundConfig.skyboxRotation[1],
        min: 0,
        max: 360,
        step: 1,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleSkyboxRotationChange(1, nextValue);
        },
      },
      "Rotation Z": {
        value: backgroundConfig.skyboxRotation[2],
        min: 0,
        max: 360,
        step: 1,
        render: (get) => get('Background.Skybox'),
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleSkyboxRotationChange(2, nextValue);
        },
      },
    }),
    [backgroundConfig, handleBackgroundChange, handleSkyboxRotationChange]
  );

  useControls(
    "Preview Waves",
    () => ({
      "Center X": {
        value: previewModelConfig.wavesCenter.x,
        min: -10,
        max: 10,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleWavesCenterChange('x', nextValue);
        },
      },
      "Center Y": {
        value: previewModelConfig.wavesCenter.y,
        min: -10,
        max: 10,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleWavesCenterChange('y', nextValue);
        },
      },
      "Center Z": {
        value: previewModelConfig.wavesCenter.z,
        min: -10,
        max: 10,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleWavesCenterChange('z', nextValue);
        },
      },
      "Min Distance": {
        value: previewModelConfig.wavesMinDistance,
        min: 0,
        max: 1,
        step: 0.01,
        hint: "Minimum distance from center (0-1, as fraction of model size)",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesMinDistance', nextValue);
        },
      },
      "Amplitude": {
        value: previewModelConfig.wavesAmplitude,
        min: 1,
        max: 10,
        step: 0.1,
        hint: "Wave width (1-10)",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesAmplitude', nextValue);
        },
      },
      "Period": {
        value: previewModelConfig.wavesPeriod,
        min: 1,
        max: 10,
        step: 0.1,
        hint: "Number of simultaneous waves",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesPeriod', nextValue);
        },
      },
      "Speed": {
        value: previewModelConfig.wavesSpeed,
        min: 0.1,
        max: 5,
        step: 0.1,
        hint: "Wave propagation speed",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesSpeed', nextValue);
        },
      },
      "Color Intensity": {
        value: previewModelConfig.wavesColorIntensity,
        min: 0,
        max: 10,
        step: 0.1,
        hint: "Wave color intensity (0-10)",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesColorIntensity', nextValue);
        },
      },
      "Displacement": {
        value: previewModelConfig.wavesDisplacement,
        min: 0,
        max: 0.1,
        step: 0.001,
        hint: "Particle displacement (0-0.1)",
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handlePreviewWavesChange('wavesDisplacement', nextValue);
        },
      },
    }),
    [previewModelConfig, handleWavesCenterChange, handlePreviewWavesChange]
  );
  useControls(
    "Teller Assistant / Transform",
    () => ({
      "Rotation Y": {
        value: tellerAssistantConfig.transform.rotationY,
        min: -Math.PI * 2,
        max: Math.PI * 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantTransformChange('rotationY', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.transform.rotationY, handleTellerAssistantTransformChange]
  );

  useControls(
    "Teller Assistant / Desktop",
    () => ({
      Scale: {
        value: tellerAssistantConfig.transform.desktop.scale,
        min: 0.0001,
        max: 0.01,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantDesktopChange('scale', nextValue);
        },
      },
      "Hover Amplitude": {
        value: tellerAssistantConfig.transform.desktop.hoverAmplitude,
        min: 0,
        max: 0.02,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantDesktopChange('hoverAmplitude', nextValue);
        },
      },
      "Padding Right (px)": {
        value: tellerAssistantConfig.transform.desktop.paddingRight,
        min: 0,
        max: 500,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantDesktopChange('paddingRight', nextValue);
        },
      },
      "Vertical Offset (px)": {
        value: tellerAssistantConfig.transform.desktop.verticalOffset,
        min: -300,
        max: 300,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantDesktopChange('verticalOffset', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.transform.desktop, handleTellerAssistantDesktopChange]
  );

  useControls(
    "Teller Assistant / Mobile",
    () => ({
      Scale: {
        value: tellerAssistantConfig.transform.mobile.scale,
        min: 0.0001,
        max: 0.01,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMobileChange('scale', nextValue);
        },
      },
      "Hover Amplitude": {
        value: tellerAssistantConfig.transform.mobile.hoverAmplitude,
        min: 0,
        max: 0.02,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMobileChange('hoverAmplitude', nextValue);
        },
      },
      "Padding Right (px)": {
        value: tellerAssistantConfig.transform.mobile.paddingRight,
        min: 0,
        max: 500,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMobileChange('paddingRight', nextValue);
        },
      },
      "Vertical Offset (px)": {
        value: tellerAssistantConfig.transform.mobile.verticalOffset,
        min: -300,
        max: 300,
        step: 1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMobileChange('verticalOffset', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.transform.mobile, handleTellerAssistantMobileChange]
  );

  useControls(
    "Teller Assistant / Mesh",
    () => ({
      "Goo Scale": {
        value: tellerAssistantConfig.mesh.gooScale,
        min: 0,
        max: 50,
        step: 0.1,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('gooScale', nextValue);
        },
      },
      "Goo Amount": {
        value: tellerAssistantConfig.mesh.gooAmount,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('gooAmount', nextValue);
        },
      },
      "Goo Speed": {
        value: tellerAssistantConfig.mesh.gooSpeed,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('gooSpeed', nextValue);
        },
      },
      "Goo Pole Amount": {
        value: tellerAssistantConfig.mesh.gooPoleAmount,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('gooPoleAmount', nextValue);
        },
      },
      "Wave Scale": {
        value: tellerAssistantConfig.mesh.waveScale,
        min: 0,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('waveScale', nextValue);
        },
      },
      "Wave Frequency": {
        value: tellerAssistantConfig.mesh.waveFrequency,
        min: 0,
        max: 10,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('waveFrequency', nextValue);
        },
      },
      "Wave Amount": {
        value: tellerAssistantConfig.mesh.waveAmount,
        min: 0,
        max: 5,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('waveAmount', nextValue);
        },
      },
      "Wave Speed": {
        value: tellerAssistantConfig.mesh.waveSpeed,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('waveSpeed', nextValue);
        },
      },
      "Wave Pole Amount": {
        value: tellerAssistantConfig.mesh.wavePoleAmount,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('wavePoleAmount', nextValue);
        },
      },
      Color: {
        value: tellerAssistantConfig.mesh.color,
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('color', nextValue);
        },
      },
      "Rotation Speed": {
        value: tellerAssistantConfig.mesh.rotationSpeed,
        min: 0,
        max: 0.01,
        step: 0.0001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('rotationSpeed', nextValue);
        },
      },
      "Show Normals": {
        value: tellerAssistantConfig.mesh.showNormals,
        onChange: (nextValue: boolean, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantMeshChange('showNormals', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.mesh, handleTellerAssistantMeshChange]
  );

  useControls(
    "Teller Assistant / Shell",
    () => ({
      "Particles Count": {
        value: tellerAssistantConfig.shell.particlesCount,
        min: 1000,
        max: 50000,
        step: 1000,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesCount', nextValue);
        },
      },
      "Particles Color": {
        value: tellerAssistantConfig.shell.particlesColor,
        onChange: (nextValue: string, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesColor', nextValue);
        },
      },
      "Particles Size": {
        value: tellerAssistantConfig.shell.particlesSize,
        min: 0,
        max: 0.1,
        step: 0.001,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesSize', nextValue);
        },
      },
      "Particles Radius": {
        value: tellerAssistantConfig.shell.particlesRadius,
        min: 0.5,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesRadius', nextValue);
        },
      },
      "Particles Noise Scale": {
        value: tellerAssistantConfig.shell.particlesNoiseScale,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesNoiseScale', nextValue);
        },
      },
      "Particles Noise Speed": {
        value: tellerAssistantConfig.shell.particlesNoiseSpeed,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesNoiseSpeed', nextValue);
        },
      },
      "Particles Noise Amount": {
        value: tellerAssistantConfig.shell.particlesNoiseAmount,
        min: 0,
        max: 2,
        step: 0.01,
        onChange: (nextValue: number, _path: string, context: ChangeContext) => {
          if (context.initial) return;
          handleTellerAssistantShellChange('particlesNoiseAmount', nextValue);
        },
      },
    }),
    [tellerAssistantConfig.shell, handleTellerAssistantShellChange]
  );

  useControls(
    "Teller Assistant / Lighting",
    // @ts-ignore Just for now
    () => {
      const isSimple = tellerAssistantConfig.lighting.lightingType === 'simple';
      
      if (isSimple) {
        return {
          "Lighting Type": {
            value: tellerAssistantConfig.lighting.lightingType,
            options: {
              Simple: "simple",
              Environment: "env",
            },
            onChange: (nextValue: string, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantLightingTypeChange(nextValue as 'simple' | 'env');
            },
          },
          "Light X": {
            value: tellerAssistantConfig.lighting.simple.lightX,
            min: -5,
            max: 5,
            step: 0.1,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightChange('lightX', nextValue);
            },
          },
          "Light Y": {
            value: tellerAssistantConfig.lighting.simple.lightY,
            min: -5,
            max: 5,
            step: 0.1,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightChange('lightY', nextValue);
            },
          },
          "Light Z": {
            value: tellerAssistantConfig.lighting.simple.lightZ,
            min: -5,
            max: 5,
            step: 0.1,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightChange('lightZ', nextValue);
            },
          },
          "Ambient Light Color": {
            value: tellerAssistantConfig.lighting.simple.ambientLightColor,
            onChange: (nextValue: string, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightColorChange('ambientLightColor', nextValue);
            },
          },
          "Directional Light Color": {
            value: tellerAssistantConfig.lighting.simple.directionalLightColor,
            onChange: (nextValue: string, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightColorChange('directionalLightColor', nextValue);
            },
          },
          "Ambient Light Intensity": {
            value: tellerAssistantConfig.lighting.simple.ambientLightIntensity,
            min: 0,
            max: 2,
            step: 0.01,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightIntensityChange('ambientLightIntensity', nextValue);
            },
          },
          "Directional Light Intensity": {
            value: tellerAssistantConfig.lighting.simple.directionalLightIntensity,
            min: 0,
            max: 5,
            step: 0.01,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantSimpleLightIntensityChange('directionalLightIntensity', nextValue);
            },
          },
        };
      } else {
        return {
          "Lighting Type": {
            value: tellerAssistantConfig.lighting.lightingType,
            options: {
              Simple: "simple",
              Environment: "env",
            },
            onChange: (nextValue: string, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantLightingTypeChange(nextValue as 'simple' | 'env');
            },
          },
          "Environment Intensity": {
            value: tellerAssistantConfig.lighting.env.environmentLightingIntensity,
            min: 0,
            max: 20,
            step: 0.1,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantEnvLightChange('environmentLightingIntensity', nextValue);
            },
          },
          "Environment Rotation Y": {
            value: tellerAssistantConfig.lighting.env.environmentRotationY,
            min: -Math.PI,
            max: Math.PI,
            step: 0.01,
            onChange: (nextValue: number, _path: string, context: ChangeContext) => {
              if (context.initial) return;
              handleTellerAssistantEnvLightChange('environmentRotationY', nextValue);
            },
          },
        };
      }
    },
    [tellerAssistantConfig.lighting.lightingType, tellerAssistantConfig.lighting.simple.lightX, tellerAssistantConfig.lighting.simple.lightY, tellerAssistantConfig.lighting.simple.lightZ, tellerAssistantConfig.lighting.simple.ambientLightColor, tellerAssistantConfig.lighting.simple.directionalLightColor, tellerAssistantConfig.lighting.simple.ambientLightIntensity, tellerAssistantConfig.lighting.simple.directionalLightIntensity, tellerAssistantConfig.lighting.env.environmentLightingIntensity, tellerAssistantConfig.lighting.env.environmentRotationY, handleTellerAssistantLightingTypeChange, handleTellerAssistantSimpleLightChange, handleTellerAssistantSimpleLightColorChange, handleTellerAssistantSimpleLightIntensityChange, handleTellerAssistantEnvLightChange]
  );

  useControls(
    "App Config",
    () => ({
      "Reset to Defaults": button(() => resetConfig()),
    }),
    [resetConfig]
  );

  return null;
}
